import { useMemo, useState } from "react";

// Hex-tile US map — approximate geographic layout using [row, col] coords.
// Update the `ESTABLISHED` set as bases are opened in each state.
const STATES = [
  // row 0
  { code: "AK", name: "Alaska", r: 0, c: 0 },
  { code: "ME", name: "Maine", r: 0, c: 11 },
  // row 1
  { code: "VT", name: "Vermont", r: 1, c: 10 },
  { code: "NH", name: "New Hampshire", r: 1, c: 11 },
  // row 2
  { code: "WA", name: "Washington", r: 2, c: 1 },
  { code: "MT", name: "Montana", r: 2, c: 2 },
  { code: "ND", name: "North Dakota", r: 2, c: 3 },
  { code: "MN", name: "Minnesota", r: 2, c: 4 },
  { code: "WI", name: "Wisconsin", r: 2, c: 5 },
  { code: "MI", name: "Michigan", r: 2, c: 6 },
  { code: "NY", name: "New York", r: 2, c: 9 },
  { code: "MA", name: "Massachusetts", r: 2, c: 10 },
  // row 3
  { code: "OR", name: "Oregon", r: 3, c: 1 },
  { code: "ID", name: "Idaho", r: 3, c: 2 },
  { code: "WY", name: "Wyoming", r: 3, c: 3 },
  { code: "SD", name: "South Dakota", r: 3, c: 4 },
  { code: "IA", name: "Iowa", r: 3, c: 5 },
  { code: "IL", name: "Illinois", r: 3, c: 6 },
  { code: "IN", name: "Indiana", r: 3, c: 7 },
  { code: "OH", name: "Ohio", r: 3, c: 8 },
  { code: "PA", name: "Pennsylvania", r: 3, c: 9 },
  { code: "NJ", name: "New Jersey", r: 3, c: 10 },
  { code: "CT", name: "Connecticut", r: 3, c: 11 },
  // row 4
  { code: "CA", name: "California", r: 4, c: 1 },
  { code: "NV", name: "Nevada", r: 4, c: 2 },
  { code: "UT", name: "Utah", r: 4, c: 3 },
  { code: "CO", name: "Colorado", r: 4, c: 4 },
  { code: "NE", name: "Nebraska", r: 4, c: 5 },
  { code: "MO", name: "Missouri", r: 4, c: 6 },
  { code: "KY", name: "Kentucky", r: 4, c: 7 },
  { code: "WV", name: "West Virginia", r: 4, c: 8 },
  { code: "VA", name: "Virginia", r: 4, c: 9 },
  { code: "MD", name: "Maryland", r: 4, c: 10 },
  { code: "DE", name: "Delaware", r: 4, c: 11 },
  { code: "RI", name: "Rhode Island", r: 4, c: 12 },
  // row 5
  { code: "AZ", name: "Arizona", r: 5, c: 3 },
  { code: "NM", name: "New Mexico", r: 5, c: 4 },
  { code: "KS", name: "Kansas", r: 5, c: 5 },
  { code: "AR", name: "Arkansas", r: 5, c: 6 },
  { code: "TN", name: "Tennessee", r: 5, c: 7 },
  { code: "NC", name: "North Carolina", r: 5, c: 8 },
  { code: "SC", name: "South Carolina", r: 5, c: 9 },
  // row 6
  { code: "HI", name: "Hawaii", r: 6, c: 0 },
  { code: "OK", name: "Oklahoma", r: 6, c: 5 },
  { code: "LA", name: "Louisiana", r: 6, c: 6 },
  { code: "MS", name: "Mississippi", r: 6, c: 7 },
  { code: "AL", name: "Alabama", r: 6, c: 8 },
  { code: "GA", name: "Georgia", r: 6, c: 9 },
  // row 7
  { code: "TX", name: "Texas", r: 7, c: 5 },
  { code: "FL", name: "Florida", r: 7, c: 9 },
];

// Currently established bases (add as your network grows)
const ESTABLISHED = new Set(["OH"]);
const HQ = "OH";

// Layout constants
const HEX_W = 58;
const HEX_H = 66;
const HEX_ROW_H = HEX_H * 0.78; // row spacing (hex packing)
const COLS = 13;
const ROWS = 8;
const H_OFFSET = HEX_W / 2; // half-column shift on odd rows

export default function USBasesMap() {
  const [hovered, setHovered] = useState(null);

  const { established, remaining } = useMemo(() => {
    const est = STATES.filter((s) => ESTABLISHED.has(s.code));
    const rem = STATES.length - est.length;
    return { established: est.length, remaining: rem };
  }, []);

  const width = COLS * HEX_W + H_OFFSET;
  const height = ROWS * HEX_ROW_H + HEX_H * 0.3;

  return (
    <div data-testid="us-bases-map" className="w-full">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-6 text-xs">
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 bg-blue-700 border-2 border-blue-700" />
          <span className="text-slate-600 font-semibold">HQ · Columbus, Ohio</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 bg-white border-2 border-slate-300" />
          <span className="text-slate-600">Base coming</span>
        </div>
        <div className="ml-auto text-slate-500">
          <span className="font-display font-black text-slate-900 text-lg">{established}</span>
          <span className="mx-1">/</span>
          <span className="font-display font-black text-lg">50</span>
          <span className="text-[10px] uppercase tracking-widest ml-2">bases established</span>
        </div>
      </div>

      {/* Map */}
      <div className="relative w-full overflow-x-auto">
        <div className="relative mx-auto" style={{ width, height, minWidth: width }}>
          {STATES.map((s) => {
            const isHQ = s.code === HQ;
            const isEstablished = ESTABLISHED.has(s.code);
            const shift = s.r % 2 === 1 ? H_OFFSET : 0;
            const x = s.c * HEX_W + shift;
            const y = s.r * HEX_ROW_H;
            const isHovered = hovered === s.code;
            return (
              <button
                type="button"
                key={s.code}
                data-testid={`state-${s.code}`}
                onMouseEnter={() => setHovered(s.code)}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setHovered(s.code)}
                onBlur={() => setHovered(null)}
                className="group absolute flex items-center justify-center transition-all duration-200 focus:outline-none"
                style={{
                  left: `${x}px`,
                  top: `${y}px`,
                  width: `${HEX_W}px`,
                  height: `${HEX_H}px`,
                  clipPath:
                    "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                }}
                aria-label={`${s.name}${isHQ ? " — headquarters" : isEstablished ? " — base established" : " — base coming"}`}
              >
                <span
                  className={`absolute inset-[3px] transition-colors duration-200 ${
                    isHQ
                      ? "bg-blue-700 group-hover:bg-blue-800"
                      : isEstablished
                        ? "bg-blue-500 group-hover:bg-blue-600"
                        : "bg-white group-hover:bg-slate-100 border border-slate-200"
                  }`}
                  style={{
                    clipPath:
                      "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                  }}
                />
                <span
                  className={`relative font-display font-black text-[11px] tracking-tight z-10 ${
                    isHQ || isEstablished ? "text-white" : "text-slate-500 group-hover:text-slate-900"
                  }`}
                >
                  {s.code}
                </span>
                {isHovered && (
                  <span className="absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap bg-slate-900 text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 z-20 pointer-events-none">
                    {s.name}
                    {isHQ ? " · HQ" : isEstablished ? " · Live" : ""}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <p className="mt-6 text-xs text-slate-500 max-w-2xl">
        A network of {remaining} bases still to build. Help us plant Feeding Blue in your state.
      </p>
    </div>
  );
}
